import React, { useState, useEffect } from 'react';
import PetAvatarEditor from './PetAvatarEditor';
import { PetAvatarConfig } from '../../types/user';

interface PetAvatarModalEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (petConfig: PetAvatarConfig, seed: string) => Promise<void>;
  initialSeed: string;
  initialConfig?: PetAvatarConfig;
  title: string;
  petName: string;
}

const PetAvatarModalEditor: React.FC<PetAvatarModalEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  initialSeed,
  initialConfig,
  title,
  petName
}) => {
  const [currentConfig, setCurrentConfig] = useState<PetAvatarConfig | null>(null);
  const [currentSvg, setCurrentSvg] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isColorPickerActive, setIsColorPickerActive] = useState(false);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Solo cerrar si se hace click directamente en el área de padding (overlay)
    // y no hay un color picker activo
    const target = e.target as HTMLElement;
    const isOverlayArea = target === e.currentTarget;
    const isColorPickerElement = target.tagName === 'INPUT' && target.getAttribute('type') === 'color';
    const isInsideColorPicker = target.closest('input[type="color"]') !== null;
    
    if (isOverlayArea && !isColorPickerElement && !isInsideColorPicker && !isColorPickerActive) {
      onClose();
    }
  };

  // Listener global para detectar cuando se está usando un color picker
  useEffect(() => {
    // Escuchar cuando se abre un color picker
    const handleFocusCapture = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'INPUT' && target.getAttribute('type') === 'color') {
        setIsColorPickerActive(true);
      }
    };
    
    // Escuchar cuando se cierra un color picker
    const handleBlurCapture = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'INPUT' && target.getAttribute('type') === 'color') {
        // Pequeño delay para permitir clicks en el color picker modal
        setTimeout(() => setIsColorPickerActive(false), 200);
      }
    };
    
    document.addEventListener('focusin', handleFocusCapture);
    document.addEventListener('focusout', handleBlurCapture);
    
    return () => {
      document.removeEventListener('focusin', handleFocusCapture);
      document.removeEventListener('focusout', handleBlurCapture);
    };
  }, []);

  // Generar SVG inicial
  useEffect(() => {
    console.log('🎯 Modal useEffect - initialConfig:', initialConfig);
    if (initialConfig) {
      import('../../utils/DirectAnimalAvatar').then(({ DirectAnimalAvatar }) => {
        try {
          const fullConfig = {
            ...DirectAnimalAvatar.getDefaultConfig(),
            ...initialConfig,
            isRound: true,
            size: 150,
            useIndividualColors: true
          };
          console.log('🎯 Generating initial avatar with config:', fullConfig);
          const svg = DirectAnimalAvatar.generateSVG(fullConfig);
          console.log('🎯 Generated SVG:', svg ? 'Success' : 'Failed');
          setCurrentSvg(svg);
        } catch (error) {
          console.error('🎯 Error generating initial SVG:', error);
        }
      });
    } else {
      // Si no hay configuración inicial, generar un avatar por defecto
      import('../../utils/DirectAnimalAvatar').then(({ DirectAnimalAvatar }) => {
        try {
          const defaultConfig = {
            ...DirectAnimalAvatar.getDefaultConfig(),
            isRound: true,
            size: 150,
            useIndividualColors: true
          };
          console.log('🎯 Generating default avatar');
          const svg = DirectAnimalAvatar.generateSVG(defaultConfig);
          setCurrentSvg(svg);
        } catch (error) {
          console.error('🎯 Error generating default SVG:', error);
        }
      });
    }
  }, [initialConfig]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!currentConfig) return;
    
    setIsSaving(true);
    try {
      await onSave(currentConfig, initialSeed);
      onClose();
    } catch (error) {
      console.error('Error saving pet avatar:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={handleOverlayClick}
      style={{ zIndex: 40 }}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-lg max-w-7xl w-full h-[90vh] my-4 shadow-2xl flex flex-col lg:flex-row"
      >
        {/* Sidebar con avatar y botones - arriba en mobile, izquierda en desktop */}
        <div className="w-full lg:w-80 flex flex-col bg-white dark:bg-gray-900 lg:border-r border-b lg:border-b-0 border-gray-200 dark:border-gray-700 lg:rounded-l-lg">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">{title}</h2>
          </div>
          
          {/* Avatar persistente */}
          <div className="p-4 lg:p-6 lg:flex-1 flex flex-row lg:flex-col items-center justify-center bg-gray-50 dark:bg-gray-800">
            <div className="mb-0 lg:mb-4 mr-4 lg:mr-0 bg-white dark:bg-gray-700 p-3 lg:p-4 rounded-xl border border-gray-200 dark:border-gray-600">
              <div className="flex justify-center">
                <div className="w-24 h-24 lg:w-40 lg:h-40 flex items-center justify-center">
                  {currentSvg ? (
                    <div 
                      className="w-24 h-24 lg:w-40 lg:h-40 rounded-full border-4 border-white dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800 shadow-2xl flex items-center justify-center avatar-preview"
                      dangerouslySetInnerHTML={{ __html: currentSvg }}
                    />
                  ) : (
                    <div className="w-24 h-24 lg:w-40 lg:h-40 rounded-full border-4 border-white dark:border-gray-600 bg-gradient-to-br from-blue-400 to-blue-600 shadow-2xl flex items-center justify-center">
                      <div className="text-white text-2xl lg:text-4xl">🐾</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!currentSvg && (
              <div className="text-center text-sm text-gray-600 dark:text-gray-400 flex-1 lg:flex-none">
                Cargando avatar...
              </div>
            )}
          </div>
          
          {/* Botones */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-row lg:flex-col gap-3">
            <button
              onClick={onClose}
              className="flex-1 lg:flex-none px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm lg:text-base"
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!currentConfig || isSaving}
              className="flex-1 lg:flex-none px-4 lg:px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl text-sm lg:text-base"
            >
              {isSaving && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75" />
                </svg>
              )}
              {isSaving ? 'Guardando...' : '✨ Guardar'}
            </button>
          </div>
          
          {/* Botón cerrar en esquina */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            disabled={isSaving}
            title="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Área principal con controles */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800 lg:rounded-r-lg">
          <div className="p-4 lg:p-6">
            <PetAvatarEditor
              initialConfig={initialConfig}
              onConfigChange={(config, svg) => {
                console.log('🎯 Modal receiving config update:', config);
                console.log('🎯 Modal receiving SVG:', svg ? 'Has SVG' : 'No SVG');
                setCurrentConfig(config);
                if (svg) {
                  setCurrentSvg(svg);
                }
              }}
              baseSeed={initialSeed}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetAvatarModalEditor; 